/**
 * Message Composer Component for Re:Nav-96
 *
 * A rich message composition interface for posting messages to group message boards.
 * Supports text input, character limits, keyboard shortcuts, and graceful error handling.
 *
 * Features:
 * - Textarea with auto-resize capability
 * - Character count with limit warning
 * - Keyboard shortcut (Cmd/Ctrl+Enter) to submit
 * - Loading state during submission
 * - Authentication and permission checks
 * - Accessible form with proper ARIA attributes
 *
 * @example
 * ```tsx
 * <MessageComposer
 *   groupId="group-123"
 *   allowMemberPosts={true}
 *   onMessageSent={(message) => console.log('Posted:', message)}
 * />
 * ```
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { messagesApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import type { Message } from '@/types';
import './MessageComposer.css';

/**
 * Maximum characters allowed in a message
 */
const MAX_MESSAGE_LENGTH = 2000;

/**
 * Threshold at which to show character warning (percentage of max)
 */
const WARNING_THRESHOLD = 0.9;

/**
 * Props for the MessageComposer component
 */
export interface MessageComposerProps {
  /** ID of the group to post messages to */
  groupId: string;
  /** Whether regular members can post (vs admin-only) */
  allowMemberPosts: boolean;
  /** Callback when a message is successfully sent */
  onMessageSent: (message: Message) => void;
  /** Optional placeholder text */
  placeholder?: string;
  /** Optional CSS class name */
  className?: string;
  /** Whether the group requires message approval */
  requireApproval?: boolean;
}

/**
 * Send icon component for the submit button
 */
function SendIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
      />
    </svg>
  );
}

/**
 * Loading spinner component
 */
function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * MessageComposer Component
 *
 * Provides a rich text input for composing and posting messages to groups.
 * Handles authentication state, permissions, and submission workflow.
 *
 * @param props - Component props
 * @returns The message composer component
 */
export function MessageComposer({
  groupId,
  allowMemberPosts,
  onMessageSent,
  placeholder = 'Write a message...',
  className = '',
  requireApproval = false,
}: MessageComposerProps) {
  const { isAuthenticated, user } = useAuth();
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Character count and warning state
  const characterCount = content.length;
  const isNearLimit = characterCount >= MAX_MESSAGE_LENGTH * WARNING_THRESHOLD;
  const isOverLimit = characterCount > MAX_MESSAGE_LENGTH;
  const remainingChars = MAX_MESSAGE_LENGTH - characterCount;

  /**
   * Auto-resize textarea based on content
   */
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to recalculate
      textarea.style.height = 'auto';
      // Set height to scrollHeight, but constrain to min/max
      const newHeight = Math.min(Math.max(textarea.scrollHeight, 80), 200);
      textarea.style.height = `${newHeight}px`;
    }
  }, [content]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      const trimmedContent = content.trim();

      // Validate content
      if (!trimmedContent) {
        return;
      }

      if (isOverLimit) {
        setError(`Message exceeds ${MAX_MESSAGE_LENGTH} character limit`);
        return;
      }

      if (isSubmitting) {
        return;
      }

      try {
        setIsSubmitting(true);
        setError(null);

        const newMessage = await messagesApi.create(groupId, {
          content: trimmedContent,
        });

        // Clear content on success
        setContent('');

        // Reset textarea height
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }

        // Notify parent component
        onMessageSent(newMessage);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to send message. Please try again.';
        setError(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [content, groupId, isOverLimit, isSubmitting, onMessageSent]
  );

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Submit on Cmd/Ctrl + Enter
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  /**
   * Handle content change
   */
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    // Clear error when user starts typing
    setError(null);
  }, []);

  // Not authenticated - show login prompt
  if (!isAuthenticated) {
    return (
      <div className={`composer-login-prompt ${className}`}>
        <svg
          className="w-5 h-5 text-white/40 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <span className="text-white/50 text-sm">Please log in to post messages.</span>
      </div>
    );
  }

  // Member posts not allowed - show disabled state
  if (!allowMemberPosts) {
    return (
      <div className={`composer-disabled ${className}`}>
        <svg
          className="w-5 h-5 text-white/40 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
          />
        </svg>
        <span className="text-white/50 text-sm">Only admins can post in this group.</span>
      </div>
    );
  }

  const canSubmit = content.trim().length > 0 && !isOverLimit && !isSubmitting;

  return (
    <form onSubmit={handleSubmit} className={`composer-form ${className}`}>
      {/* Error message */}
      {error && (
        <div className="composer-error" role="alert">
          <svg
            className="w-4 h-4 text-red-400 mr-2 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-red-400 text-sm">{error}</span>
        </div>
      )}

      {/* Moderation notice */}
      {requireApproval && (
        <div className="composer-moderation-notice">
          <svg
            className="w-4 h-4 text-yellow-500 mr-2 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="text-yellow-500/80 text-xs">
            Messages in this group require approval before appearing.
          </span>
        </div>
      )}

      {/* Input area */}
      <div className="composer-input-wrapper">
        {/* User avatar */}
        <div className="composer-avatar" aria-hidden="true">
          {user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt=""
              className="w-full h-full object-cover rounded-full"
            />
          ) : (
            <span className="composer-avatar-initials">
              {user?.displayName
                ?.split(' ')
                .map((n) => n[0])
                .join('')
                .substring(0, 2)
                .toUpperCase() || '?'}
            </span>
          )}
        </div>

        {/* Textarea and submit */}
        <div className="composer-input-container">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`composer-input ${isOverLimit ? 'composer-input-error' : ''}`}
            rows={2}
            disabled={isSubmitting}
            aria-label="Message content"
            aria-describedby="composer-hint"
            aria-invalid={isOverLimit}
            maxLength={MAX_MESSAGE_LENGTH + 100} // Allow slight overflow for error display
          />

          {/* Character count and hint */}
          <div className="composer-footer">
            <span id="composer-hint" className="composer-hint">
              Press{' '}
              <kbd className="composer-kbd">
                {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}
              </kbd>
              +<kbd className="composer-kbd">Enter</kbd> to send
            </span>

            {(isNearLimit || content.length > 0) && (
              <span
                className={`composer-char-count ${
                  isOverLimit
                    ? 'composer-char-over'
                    : isNearLimit
                    ? 'composer-char-warning'
                    : ''
                }`}
              >
                {remainingChars < 0 ? remainingChars : characterCount}/{MAX_MESSAGE_LENGTH}
              </span>
            )}
          </div>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={!canSubmit}
          className="composer-submit"
          aria-label={isSubmitting ? 'Sending message...' : 'Send message'}
        >
          {isSubmitting ? (
            <LoadingSpinner className="w-5 h-5 animate-spin" />
          ) : (
            <SendIcon className="w-5 h-5" />
          )}
        </button>
      </div>
    </form>
  );
}

export default MessageComposer;
